import { Outlet, useParams, useSearchParams } from 'react-router-dom';
import DefaultReconciliationWorksheet from '../features/worksheet-v2/DefaultReconciliationWorksheet';
import WorksheetProvider from '../providers/WorksheetProvider';
import { useWorksheet } from '../queries/use-worksheet';
import { WorksheetTypeEnum } from '../types/worksheet';
import Dropzone from '../components/dropzone/Dropzone';
import { useCreateDocument } from '../queries/use-create-document';
import { toastNotification } from '../components/toast/Toast';
import queryClient from '../query-client';
import { Spinner } from '@cimplico/components';
import { useEffect, useState } from 'react';
import DefaultGenericWorksheet from '../features/worksheet-v2/DefaultGenericWorksheet';

import ClipboardImageUploadConfirm from '../features/files/ConfirmationModals/ClipboardImageUploadConfirm';
import { useWorkpaperContext } from '../providers/WorkpaperProvider';

function WorksheetPageV2(): JSX.Element {
    const { id } = useParams();
    const { isLoading, data, isError, isSuccess } = useWorksheet(id);
    const { readOnly, priorWorkpaperId } = useWorkpaperContext();
    const priorWorksheetQuery = useWorksheet(
        data?.worksheet?.priorWorksheetId || undefined,
        priorWorkpaperId,
        Boolean(data?.worksheet?.priorWorksheetId && priorWorkpaperId)
    );
    const createDocumentQuery = useCreateDocument();
    const [clipboardFile, setClipboardFile] = useState<File>();
    const [isClipboardImage, setIsClipboardImage] = useState(false);
    const [search, setSearch] = useSearchParams();
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (search.get('folder') && id) {
            search.set('folder', id);
            setSearch(search);
        }
    }, [id]);

    /**
     * Uploads dropped files to the worksheet folder
     * @param files
     */
    async function handleFileDrop(files: File[]): Promise<void> {
        const uploads = [];
        setUploading(true);
        for (const file of files) {
            const uploadPromise = createDocumentQuery.mutateAsync(
                { file, parentFolderId: worksheet!.id },
                {
                    onSuccess: () => {
                        toastNotification('success', 'New file uploaded!');
                    },
                }
            );
            uploads.push(uploadPromise);
        }
        await Promise.allSettled(uploads);

        queryClient.invalidateQueries(['worksheets', worksheet!.id]);
        setUploading(false);
    }

    // Reset clipboard file
    function handleClipboardConfirmClose(): void {
        setUploading(false);
        setIsClipboardImage(false);
        setClipboardFile(undefined);
    }

    /**
     * Handles when the user pastes a file from their clipboard. This is most commonly screenshots.
     * @param files
     */
    function handlePaste(files: File[]): void {
        if (files && files[0]) {
            setClipboardFile(files[0]);
            if (files[0].type.includes('image')) {
                setIsClipboardImage(true);
            } else {
                handleFileDrop(files);
            }
        }
    }

    /**
     * Upload the file from the user's clipboard with the new name they have selected.
     *
     * @param name
     */
    async function uploadClipboardFile(name: string): Promise<void> {
        if (!clipboardFile) {
            return;
        }
        const file = new File([clipboardFile], name, { type: clipboardFile.type });

        handleClipboardConfirmClose();

        await handleFileDrop([file]);
    }

    if (isLoading) {
        return <></>;
    }

    if (isError || !isSuccess) {
        return <p>Error</p>;
    }

    const { worksheet } = data;
    const priorWorksheet = data.worksheet.priorWorksheetId ? priorWorksheetQuery.data?.worksheet : undefined;

    return (
        <>
            {uploading && (
                <div className="pointer-events-none absolute top-0 left-0 w-full h-full z-50 bg-white/60">
                    <div className="absolute top-1/3 left-1/2">
                        <Spinner />
                    </div>
                </div>
            )}
            <div className="flex h-full pb-6">
                {isClipboardImage && <ClipboardImageUploadConfirm onUpload={uploadClipboardFile} onClose={handleClipboardConfirmClose} />}
                <Dropzone onDrop={handleFileDrop} onPaste={handlePaste} disabled={readOnly}>
                    <div className="flex min-h-full w-full flex-col">
                        <WorksheetProvider worksheet={worksheet} priorWorksheet={priorWorksheet}>
                            {worksheet.type === WorksheetTypeEnum.RECONCILIATION ? (
                                <DefaultReconciliationWorksheet />
                            ) : (
                                <DefaultGenericWorksheet />
                            )}
                        </WorksheetProvider>
                    </div>
                </Dropzone>
            </div>
        </>
    );
}

export default WorksheetPageV2;
